import {
  addMonths, addQuarters, addYears, format, getQuarter, getYear,
  startOfMonth, startOfQuarter, startOfYear,
} from "date-fns";
import { enUS, fr } from "date-fns/locale";
import type { Language } from "../../../client/i18n/translations";
import type { ReviewPeriod } from "../../../social-reports/metrics/types";
import { capitalize } from "../../lib/format";

/** Début de la période (mois, trimestre, année) contenant `d`. */
export function periodStartOf(period: ReviewPeriod, d: Date): Date {
  switch (period) {
    case "monthly": return startOfMonth(d);
    case "quarterly": return startOfQuarter(d);
    case "annual": return startOfYear(d);
  }
}

/** Décale `d` de `n` périodes (négatif = en arrière). */
export function shiftPeriod(period: ReviewPeriod, d: Date, n: number): Date {
  switch (period) {
    case "monthly": return addMonths(d, n);
    case "quarterly": return addQuarters(d, n);
    case "annual": return addYears(d, n);
  }
}

/** La période de `d` est-elle la période courante (ou future) ? Sert à bloquer « suivant ». */
export function isCurrentOrFuture(period: ReviewPeriod, d: Date, now = new Date()): boolean {
  return periodStartOf(period, d) >= periodStartOf(period, now);
}

/** Libellé lisible d'une période : « Octobre 2026 », « T4 2026 », « 2026 ». */
export function periodLabel(period: ReviewPeriod, d: Date, lang: Language): string {
  switch (period) {
    case "monthly": return capitalize(format(d, "MMMM yyyy", { locale: lang === "fr" ? fr : enUS }));
    case "quarterly": return `${lang === "fr" ? "T" : "Q"}${getQuarter(d)} ${getYear(d)}`;
    case "annual": return String(getYear(d));
  }
}

/** Les bornes d'un bilan sont calculées en UTC côté serveur (resolvePeriodRangeUtc) :
 *  on relit le jour civil UTC de `periodStart` pour l'étiqueter, sinon un fuseau
 *  négatif afficherait le mois précédent. */
export function utcCalendarDate(iso: string | Date): Date {
  const d = new Date(iso);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Date de référence envoyée au serveur : midi UTC au milieu de la période choisie,
 *  pour qu'aucun décalage de fuseau ne fasse basculer le mois. */
export function referenceIso(period: ReviewPeriod, d: Date): string {
  const s = periodStartOf(period, d);
  return new Date(Date.UTC(s.getFullYear(), s.getMonth(), 15, 12)).toISOString();
}

/** Période d'un rapport existant, ou null si elle n'est pas un bilan (daily / weekly). */
export function asReviewPeriod(p: string): ReviewPeriod | null {
  return p === "monthly" || p === "quarterly" || p === "annual" ? p : null;
}
